import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { eur, num } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Loader2, Pencil, Building2, ArrowRightCircle, Handshake } from "lucide-react";
import { toast } from "sonner";
import ActivityLog from "@/components/ActivityLog";
import { QuotesForClient } from "@/components/QuotesForClient";
import ProspectFormDialog from "@/components/ProspectFormDialog";
import ProspectFunnelCard from "@/components/ProspectFunnelCard";
import ConsulenzeAnagrafica from "@/components/ConsulenzeAnagrafica";
import ConsulenzeTecnicheAnagrafica from "@/components/ConsulenzeTecnicheAnagrafica";
import ConvertPartnerDialog from "@/components/ConvertPartnerDialog";
import AffidamentoCard from "@/components/AffidamentoCard";
import { mergeReferenti } from "@/lib/referenti";

const STAGE_LABEL = { nuovo: "Non contattato", contattato: "Primo contatto", qualificato: "Parlato col decision maker", trattativa: "Incontro presso cliente/azienda", vinto: "Partner", ordine: "Ordine", perso: "Perso" };
const itDate = (d) => (d ? String(d).slice(0, 10).split("-").reverse().join("/") : "—");

const Row = ({ label, value }) => (
  <div className="flex items-center justify-between border-b border-border py-2 text-sm">
    <span className="text-muted-foreground">{label}</span>
    <span className="font-semibold">{value ?? "—"}</span>
  </div>
);
const ND = <span className="text-muted-foreground">N/D</span>;
const SubHead = ({ children }) => (
  <h3 className="mb-1 mt-6 font-mono text-[11px] font-bold uppercase tracking-widest text-primary first:mt-2">{children}</h3>
);

export default function ProspectDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const canAff = ["admin", "back_office"].includes(user?.role) || user?.role_actual === "back_office" || (user?.extra_pages || []).includes("backoffice");
  const isBackOffice = user?.role === "back_office" || user?.role_actual === "back_office";
  const canConvert = ["admin", "commercial_manager"].includes(user?.role) || isBackOffice;
  const canEdit = canConvert || user?.role === "agency";
  const [p, setP] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [convBusy, setConvBusy] = useState(false);
  const [convPartnerOpen, setConvPartnerOpen] = useState(false);

  const load = () => { setLoading(true); api.get(`/prospects/${id}`).then((r) => setP(r.data)).catch(() => setP(null)).finally(() => setLoading(false)); };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [id]);

  const convertiCliente = async () => {
    if (!window.confirm("Convertire questo prospect in Cliente? Verranno trasferiti fido, bilancio e dati anagrafici nella nuova scheda cliente.")) return;
    setConvBusy(true);
    try { const r = await api.post(`/prospects/${id}/convert-cliente`); toast.success("Prospect convertito in Cliente"); window.open(`/partners/${r.data.partner_id}`, "_blank"); }
    catch (e) { toast.error(e.response?.data?.detail || "Errore nella conversione"); }
    finally { setConvBusy(false); }
  };

  if (loading) return <div className="flex h-64 items-center justify-center" data-testid="prospect-detail-loading"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;
  if (!p) return <div className="p-6 text-sm text-muted-foreground" data-testid="prospect-detail-empty">Prospect non trovato. <Link to="/prospects" className="text-primary hover:underline">Torna all'elenco</Link></div>;

  const indirizzo = p.address || null;
  const cittaCap = [p.cap, p.city].filter(Boolean).join(" ") || null;
  const emailsExtra = (p.emails_extra || []).filter((e) => e && e !== p.email);
  const referenti = mergeReferenti(p);
  const catLabel = p.categoria === "cliente_finale"
    ? `Cliente finale${p.tipo_cliente ? ` · ${p.tipo_cliente}` : ""}${p.settore ? ` · ${p.settore}` : ""}` : null;

  return (
    <div className="space-y-6" data-testid="prospect-detail-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Link to="/prospects" className="text-muted-foreground hover:text-foreground" data-testid="prospect-back"><ArrowLeft className="h-5 w-5" /></Link>
          <div>
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="border-amber-300 bg-amber-100 text-[10px] text-amber-800">Prospect</Badge>
              <h1 className="font-heading text-2xl font-bold tracking-tight" data-testid="prospect-name">{p.name}</h1>
              {p.converted_partner_id && <Badge className="border-green-300 bg-green-100 text-[10px] text-green-800" data-testid="prospect-converted-badge">Convertito · {itDate(p.converted_at)}</Badge>}
            </div>
            <p className="text-sm text-muted-foreground">Stage: <b>{STAGE_LABEL[p.stage] || p.stage}</b></p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {p.converted_partner_id && <Link to={`/partners/${p.converted_partner_id}`}><Button variant="outline" size="sm" data-testid="prospect-open-cliente"><Building2 className="mr-2 h-4 w-4" /> Apri scheda Cliente</Button></Link>}
          {canConvert && !p.converted_partner_id && p.stage !== "vinto" && <Button variant="outline" size="sm" onClick={convertiCliente} disabled={convBusy} data-testid="prospect-convert-btn">{convBusy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ArrowRightCircle className="mr-2 h-4 w-4 text-green-500" />} Converti in Cliente</Button>}
          {canConvert && !p.converted_partner_id && p.stage !== "vinto" && <Button variant="outline" size="sm" onClick={() => setConvPartnerOpen(true)} data-testid="prospect-convert-partner-btn"><Handshake className="mr-2 h-4 w-4 text-green-600" /> Converti in Partner</Button>}
          {canEdit && <Button size="sm" onClick={() => setEditOpen(true)} data-testid="prospect-edit-btn"><Pencil className="mr-2 h-4 w-4" /> Modifica</Button>}
        </div>
      </div>

      <ConvertPartnerDialog prospect={convPartnerOpen ? p : null} onOpenChange={(o) => { if (!o) setConvPartnerOpen(false); }} onDone={(pid) => { setConvPartnerOpen(false); if (pid) window.open(`/partners/${pid}`, "_blank"); load(); }} />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Card className="p-6" data-testid="prospect-scheda-card">
          <h2 className="mb-1 font-heading text-lg font-bold tracking-tight">Scheda Prospect</h2>

          <SubHead>Anagrafica</SubHead>
          <Row label="Territorio / Regione" value={p.region} />
          <Row label="Provincia" value={p.province} />
          <Row label="Indirizzo" value={indirizzo} />
          <Row label="Città / CAP" value={cittaCap} />
          <Row label="Agente" value={p.agency_name} />
          <Row label="Partita IVA" value={p.partita_iva} />
          <Row label="Codice Fiscale" value={p.codice_fiscale} />
          <Row label="Email" value={p.email} />
          {emailsExtra.length > 0 && <Row label="Email aggiuntive" value={emailsExtra.join(", ")} />}
          <Row label="Telefono" value={p.phone} />
          <Row label="Sito web" value={p.website ? <a href={p.website.startsWith("http") ? p.website : `http://${p.website}`} target="_blank" rel="noreferrer" className="text-primary hover:underline" data-testid="prospect-website">{p.website.replace(/^https?:\/\//, "")}</a> : null} />

          <SubHead>Dati societari</SubHead>
          <Row label="Forma giuridica" value={p.forma_giuridica} />
          <Row label="Data di costituzione" value={p.data_costituzione ? itDate(p.data_costituzione) : null} />
          <Row label="Codice ATECO" value={p.ateco} />
          <Row label="Capitale sociale" value={p.capitale_sociale != null ? eur(p.capitale_sociale) : null} />
          <Row label="Codice SDI" value={p.codice_sdi} />
          <Row label="Valutazione Creditsafe" value={(p.fascia_rischio || p.score != null) ? `${p.fascia_rischio || "—"}${p.score != null ? ` · score ${p.score}` : ""}` : null} />
          <Row label="Dipendenti" value={p.dipendenti != null ? num(p.dipendenti) : null} />
          <Row label="Fatturato" value={p.fatturato != null ? eur(p.fatturato) : null} />

          <SubHead>Dati commerciali</SubHead>
          <Row label="Categoria" value={catLabel} />
          <Row label="Fonte contatto" value={p.fonte_contatto || p.source} />
          <Row label="Potenziale acquisti annuo" value={p.potenziale_annuo != null ? eur(p.potenziale_annuo) : null} />
          <Row label="Valore atteso" value={p.expected_value != null ? eur(p.expected_value) : null} />
          <Row label="Acquisti TORRI anno" value={ND} />
          <Row label="Share of Wallet TORRI" value={ND} />
          <Row label="N° preventivi (gestionale)" value={ND} />
          <Row label="N° ordini (gestionale)" value={ND} />
          <Row label="Prossima azione" value={p.next_action} />
          <Row label="Data prossima azione" value={p.next_action_date ? itDate(p.next_action_date) : null} />

          <SubHead>Partnership · Rete Beyond</SubHead>
          <Row label="Data di attivazione" value={ND} />
          <Row label="Punti Vesta" value={ND} />
          <Row label="Contratto partnership" value={ND} />

          <SubHead>Altro</SubHead>
          {p.note_inseritore && <Row label="Note inseritore" value={p.note_inseritore} />}
          {p.stage === "perso" && <Row label="Motivo perdita" value={p.lost_reason} />}
          {p.notes ? <p className="mt-2 rounded-md bg-accent p-3 text-xs text-accent-foreground" data-testid="prospect-notes">{p.notes}</p> : <p className="py-2 text-sm text-muted-foreground">—</p>}
        </Card>

        <div className="space-y-6">
          <Card className="p-6" data-testid="prospect-referenti-card">
            <h2 className="mb-3 font-heading text-lg font-bold tracking-tight">Referenti e contatti</h2>
            {referenti.length === 0
              ? <p className="text-sm text-muted-foreground">Nessun referente.</p>
              : (
                <div className="space-y-2">
                  {referenti.map((r, i) => (
                    <div key={i} className="rounded-md border border-border p-2.5 text-sm" data-testid={`prospect-referente-${i}`}>
                      <div className="font-semibold">{r.nome || "—"}{r.ruolo ? <span className="ml-2 text-xs font-normal text-muted-foreground">{r.ruolo}</span> : null}</div>
                      <div className="text-xs text-muted-foreground">{[r.email, r.telefono].filter(Boolean).join(" · ") || "—"}</div>
                    </div>
                  ))}
                </div>
              )}
          </Card>
          <Card className="p-6">
            <QuotesForClient prospectId={p.id} testid="prospect-detail-quotes" />
          </Card>
          {canEdit && (
            <Card className="p-6">
              <ProspectFunnelCard prospect={p} onChanged={load} />
            </Card>
          )}
          <Card className="p-6">
            <ActivityLog url={`/prospects/${p.id}/activities`} title="Attività svolte sul prospect" testid="prospect-detail-activity-log" />
          </Card>
        </div>
      </div>

      {canAff && <AffidamentoCard kind="prospect" id={p.id} doc={p} onRefresh={load} />}

      <ConsulenzeAnagrafica installerId={p.id} testid="prospect-consulenze" />
      <ConsulenzeTecnicheAnagrafica installerId={p.id} testid="prospect-consulenze-tecniche" />

      <ProspectFormDialog open={editOpen} onOpenChange={setEditOpen} prospectId={id} onSaved={load} />
    </div>
  );
}
